import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import PageShell from "../dashboard/PageShell";
import { Save, X } from "lucide-react";

interface AddNewDocumentProps {}

interface DocumentFormData {
  // Row 1
  classification: string; // Phân loại (*)
  documentCategory: string; // Danh mục văn bản (*)
  issuingAuthority: string; // Cơ quan ban hành (*)
  receivedDate: string; // Ngày nhận
  sentDate: string; // Ngày gửi đi (for outgoing documents)

  // Row 2
  sendReceiveMethod: string; // Phương thức gửi/nhận
  priority: string; // Ưu tiên (*)
  documentSymbol: string; // Số hiệu văn bản
  incomingNumber: string; // Số đến
  outgoingNumber: string; // Số đi (for outgoing documents)
  day: string; // Ngày
  month: string; // Tháng
  issuanceYear: string; // Năm ban hành (*)
  receivingAuthority: string; // Cơ quan tiếp nhận (for outgoing documents)

  // Row 3
  processingDeadline: string; // Hạn xử lý
  approvingLeader: string; // Lãnh đạo phê duyệt
  documentSigner: string; // Người ký văn bản
  originalStorageLocation: string; // Nơi lưu bản chính

  // Row 4
  contentSummary: string; // Trích yếu nội dung (*)
  notes: string; // Ghi chú

  // Document Status
  status: string; // Trạng thái văn bản
  assignedTo: string; // Người được giao
  assignedDate: string; // Ngày giao
  processedBy: string; // Người xử lý
  processedDate: string; // Ngày xử lý

  // File attachment
  attachedFiles: File[];
}

const AddNewDocument: React.FC<AddNewDocumentProps> = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<DocumentFormData>({
    classification: "Văn bản đến",
    documentCategory: "",
    issuingAuthority: "",
    receivedDate: "11/09/2025",
    sentDate: "15/09/2025",
    sendReceiveMethod: "Thông thường",
    priority: "Thường",
    documentSymbol: "",
    incomingNumber: "",
    outgoingNumber: "",
    day: "",
    month: "9",
    issuanceYear: "2025",
    receivingAuthority: "",
    processingDeadline: "11/09/2025",
    approvingLeader: "",
    documentSigner: "",
    originalStorageLocation: "",
    contentSummary: "",
    notes: "",
    status: "chưa_xử_lý", // Mặc định là chưa xử lý
    assignedTo: "",
    assignedDate: "",
    processedBy: "",
    processedDate: "",
    attachedFiles: [],
  });

  // Date picker states
  const [receivedDate, setReceivedDate] = useState<Date | null>(
    new Date("2025-09-11")
  );
  const [sentDate, setSentDate] = useState<Date | null>(new Date("2025-09-15"));
  const [processingDeadline, setProcessingDeadline] = useState<Date | null>(
    new Date("2025-09-11")
  );
  const [assignedDate, setAssignedDate] = useState<Date | null>(null);
  const [processedDate, setProcessedDate] = useState<Date | null>(null);

  // Format date to dd/mm/yyyy
  const formatDate = (date: Date | null): string => {
    if (!date) return "";
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const handleInputChange = (
    field: keyof DocumentFormData,
    value: string | File[]
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value as never,
    }));
  };

  const handleFileSelect = (files: FileList | null) => {
    if (files) {
      const newFiles = Array.from(files);
      handleInputChange("attachedFiles", [
        ...formData.attachedFiles,
        ...newFiles,
      ]);
    }
  };

  const handleSave = () => {
    console.log("Saving document:", formData);
    // TODO: Implement save functionality
    navigate("/documents/incoming-list");
  };

  const handleCancel = () => {
    navigate("/documents/incoming-list");
  };

  // Mock data for dropdowns
  const classifications = ["Văn bản đến", "Văn bản đi", "Văn bản nội bộ"];

  const documentCategories = [
    "Công văn",
    "Quyết định",
    "Thông tư",
    "Nghị định",
    "Chỉ thị",
  ];

  const sendReceiveMethods = [
    "Thông thường",
    "Bưu điện",
    "Email",
    "Fax",
    "Trực tiếp",
  ];

  const priorities = [
    "Thường",
    "Gấp",
    "Hỏa tốc",
    "Mật",
    "Tuyệt mật",
    "Tối mật",
  ];

  const days = Array.from({ length: 31 }, (_, i) => `${i + 1}`);
  const months = Array.from({ length: 12 }, (_, i) => `${i + 1}`);
  const years = Array.from({ length: 11 }, (_, i) => `${2020 + i}`);

  return (
    <PageShell title="Thêm văn bản">
      <div className="space-y-6">
        {/* Header Section */}
        <div className="flex justify-between items-center">
          <h1 className="text-xl font-semibold text-gray-800">Thêm văn bản</h1>
        </div>

        {/* Form Container */}
        <div className="bg-white rounded-lg p-6">
          <div className="space-y-6">
            {/* Row 1 - 4 columns */}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phân loại <span className="text-red-500">(*)</span>
                </label>
                <select
                  value={formData.classification}
                  onChange={(e) =>
                    handleInputChange("classification", e.target.value)
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {classifications.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Danh mục văn bản <span className="text-red-500">(*)</span>
                </label>
                <input
                  type="text"
                  value={formData.documentCategory}
                  onChange={(e) =>
                    handleInputChange("documentCategory", e.target.value)
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Nhập danh mục văn bản..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cơ quan ban hành <span className="text-red-500">(*)</span>
                </label>
                <input
                  type="text"
                  value={formData.issuingAuthority}
                  onChange={(e) =>
                    handleInputChange("issuingAuthority", e.target.value)
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Nhập cơ quan ban hành..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {formData.classification === "Văn bản đi"
                    ? "Ngày gửi đi"
                    : "Ngày nhận"}
                </label>
                <input
                  type="date"
                  value={
                    formData.classification === "Văn bản đi"
                      ? sentDate
                        ? sentDate.toISOString().split("T")[0]
                        : ""
                      : receivedDate
                      ? receivedDate.toISOString().split("T")[0]
                      : ""
                  }
                  onChange={(e) => {
                    const date = e.target.value
                      ? new Date(e.target.value)
                      : null;
                    if (formData.classification === "Văn bản đi") {
                      setSentDate(date);
                      handleInputChange("sentDate", formatDate(date));
                    } else {
                      setReceivedDate(date);
                      handleInputChange("receivedDate", formatDate(date));
                    }
                  }}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Row 2 - Dynamic columns based on classification */}
            {formData.classification === "Văn bản đi" ? (
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phương thức gửi/nhận
                  </label>
                  <select
                    value={formData.sendReceiveMethod}
                    onChange={(e) =>
                      handleInputChange("sendReceiveMethod", e.target.value)
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {sendReceiveMethods.map((item) => (
                      <option key={item} value={item}>
                        {item}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ưu tiên <span className="text-red-500">(*)</span>
                  </label>
                  <select
                    value={formData.priority}
                    onChange={(e) =>
                      handleInputChange("priority", e.target.value)
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {priorities.map((item) => (
                      <option key={item} value={item}>
                        {item}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Số hiệu văn bản
                  </label>
                  <input
                    type="text"
                    value={formData.documentSymbol}
                    onChange={(e) =>
                      handleInputChange("documentSymbol", e.target.value)
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Nhập số hiệu..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Số đi
                  </label>
                  <input
                    type="text"
                    value={formData.outgoingNumber}
                    onChange={(e) =>
                      handleInputChange("outgoingNumber", e.target.value)
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Nhập số đi..."
                  />
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phương thức gửi/nhận
                  </label>
                  <select
                    value={formData.sendReceiveMethod}
                    onChange={(e) =>
                      handleInputChange("sendReceiveMethod", e.target.value)
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {sendReceiveMethods.map((item) => (
                      <option key={item} value={item}>
                        {item}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ưu tiên <span className="text-red-500">(*)</span>
                  </label>
                  <select
                    value={formData.priority}
                    onChange={(e) =>
                      handleInputChange("priority", e.target.value)
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {priorities.map((item) => (
                      <option key={item} value={item}>
                        {item}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Số hiệu văn bản
                  </label>
                  <input
                    type="text"
                    value={formData.documentSymbol}
                    onChange={(e) =>
                      handleInputChange("documentSymbol", e.target.value)
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Nhập số hiệu..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Số đến
                  </label>
                  <input
                    type="text"
                    value={formData.incomingNumber}
                    onChange={(e) =>
                      handleInputChange("incomingNumber", e.target.value)
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Nhập số đến..."
                  />
                </div>
              </div>
            )}

            {/* Row 2.5 - Date fields for both classifications */}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
              {formData.classification === "Văn bản đi" ? (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Ngày
                    </label>
                    <select
                      value={formData.day}
                      onChange={(e) => handleInputChange("day", e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">- Chọn ngày -</option>
                      {days.map((day) => (
                        <option key={day} value={day}>
                          {day}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tháng
                    </label>
                    <select
                      value={formData.month}
                      onChange={(e) =>
                        handleInputChange("month", e.target.value)
                      }
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      {months.map((month) => (
                        <option key={month} value={month}>
                          {month}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Năm ban hành <span className="text-red-500">(*)</span>
                    </label>
                    <select
                      value={formData.issuanceYear}
                      onChange={(e) =>
                        handleInputChange("issuanceYear", e.target.value)
                      }
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      {years.map((year) => (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Ngày
                    </label>
                    <select
                      value={formData.day}
                      onChange={(e) => handleInputChange("day", e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">- Chọn ngày -</option>
                      {days.map((day) => (
                        <option key={day} value={day}>
                          {day}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tháng
                    </label>
                    <select
                      value={formData.month}
                      onChange={(e) =>
                        handleInputChange("month", e.target.value)
                      }
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      {months.map((month) => (
                        <option key={month} value={month}>
                          {month}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Năm ban hành <span className="text-red-500">(*)</span>
                    </label>
                    <select
                      value={formData.issuanceYear}
                      onChange={(e) =>
                        handleInputChange("issuanceYear", e.target.value)
                      }
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      {years.map((year) => (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div></div>
                </>
              )}
            </div>

            {/* Row 3 - Dynamic columns based on classification */}
            {formData.classification === "Văn bản đi" ? (
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cơ quan tiếp nhận
                  </label>
                  <input
                    type="text"
                    value={formData.receivingAuthority}
                    onChange={(e) =>
                      handleInputChange("receivingAuthority", e.target.value)
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Nhập cơ quan tiếp nhận..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Hạn xử lý
                  </label>
                  <input
                    type="date"
                    value={
                      processingDeadline
                        ? processingDeadline.toISOString().split("T")[0]
                        : ""
                    }
                    onChange={(e) => {
                      const date = e.target.value
                        ? new Date(e.target.value)
                        : null;
                      setProcessingDeadline(date);
                      handleInputChange("processingDeadline", formatDate(date));
                    }}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nơi lưu bản chính
                  </label>
                  <input
                    type="text"
                    value={formData.originalStorageLocation}
                    onChange={(e) =>
                      handleInputChange(
                        "originalStorageLocation",
                        e.target.value
                      )
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Nhập nơi lưu trữ..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Lãnh đạo phê duyệt
                  </label>
                  <input
                    type="text"
                    value={formData.approvingLeader}
                    onChange={(e) =>
                      handleInputChange("approvingLeader", e.target.value)
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Nhập tên lãnh đạo..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Người ký văn bản
                  </label>
                  <input
                    type="text"
                    value={formData.documentSigner}
                    onChange={(e) =>
                      handleInputChange("documentSigner", e.target.value)
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Nhập tên người ký..."
                  />
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Hạn xử lý
                  </label>
                  <input
                    type="date"
                    value={
                      processingDeadline
                        ? processingDeadline.toISOString().split("T")[0]
                        : ""
                    }
                    onChange={(e) => {
                      const date = e.target.value
                        ? new Date(e.target.value)
                        : null;
                      setProcessingDeadline(date);
                      handleInputChange("processingDeadline", formatDate(date));
                    }}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Lãnh đạo phê duyệt
                  </label>
                  <input
                    type="text"
                    value={formData.approvingLeader}
                    onChange={(e) =>
                      handleInputChange("approvingLeader", e.target.value)
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Nhập tên lãnh đạo..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Người ký văn bản
                  </label>
                  <input
                    type="text"
                    value={formData.documentSigner}
                    onChange={(e) =>
                      handleInputChange("documentSigner", e.target.value)
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Nhập tên người ký..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nơi lưu bản chính
                  </label>
                  <input
                    type="text"
                    value={formData.originalStorageLocation}
                    onChange={(e) =>
                      handleInputChange(
                        "originalStorageLocation",
                        e.target.value
                      )
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Nhập nơi lưu trữ..."
                  />
                </div>
              </div>
            )}

            {/* Row 4 - 2 large text areas */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Trích yếu nội dung <span className="text-red-500">(*)</span>
                </label>
                <textarea
                  rows={4}
                  value={formData.contentSummary}
                  onChange={(e) =>
                    handleInputChange("contentSummary", e.target.value)
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  placeholder="Nhập trích yếu nội dung..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ghi chú
                </label>
                <textarea
                  rows={4}
                  value={formData.notes}
                  onChange={(e) => handleInputChange("notes", e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  placeholder="Nhập ghi chú..."
                />
              </div>
            </div>

            {/* File Upload Section */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  File đính kèm
                </label>

                {/* Custom File Input */}
                <div className="flex items-center space-x-4">
                  <input
                    type="file"
                    multiple
                    onChange={(e) => handleFileSelect(e.target.files)}
                    className="hidden"
                    id="document-file-upload"
                  />
                  <label
                    htmlFor="document-file-upload"
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 cursor-pointer transition-colors"
                  >
                    Choose Files
                  </label>
                  <span className="text-sm text-gray-500">
                    {formData.attachedFiles.length === 0
                      ? "No file chosen"
                      : `${formData.attachedFiles.length} file(s) chosen`}
                  </span>
                </div>

                {/* File List */}
                {formData.attachedFiles.length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">
                      Files:
                    </h4>
                    <div className="space-y-2">
                      {formData.attachedFiles.map((file, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between p-2 bg-white border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex items-center space-x-2 min-w-0 flex-1">
                            <svg
                              className="w-4 h-4 text-gray-400 flex-shrink-0"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                              />
                            </svg>
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-medium text-gray-900 truncate">
                                {file.name}
                              </div>
                              <div className="text-xs text-gray-500">
                                {(file.size / 1024).toFixed(1)} KB
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={() => {
                              const updatedFiles =
                                formData.attachedFiles.filter(
                                  (_, i) => i !== index
                                );
                              handleInputChange("attachedFiles", updatedFiles);
                            }}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50 p-1 rounded transition-colors flex-shrink-0"
                            title="Xóa file"
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                              />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end gap-2">
          <button
            onClick={handleCancel}
            className="bg-gray-300 text-gray-700 px-6 py-2 rounded-md text-sm font-medium hover:bg-gray-400 transition-colors flex items-center space-x-2"
          >
            <X className="w-4 h-4" />
            <span>Hủy</span>
          </button>
          <button
            onClick={handleSave}
            className="bg-green-600 text-white px-6 py-2 rounded-md text-sm font-medium hover:bg-green-700 transition-colors flex items-center space-x-2"
          >
            <Save className="w-4 h-4" />
            <span>Lưu dữ liệu</span>
          </button>
        </div>
      </div>
    </PageShell>
  );
};

export default AddNewDocument;
